<main class="page">
  <div class="wrap">

    <!-- ============ LEAD FORM ============ -->
    <ng-container *ngIf="!hasLead">
      <p class="eyebrow">Free tool</p>
      <h1 class="title">Free CSSE predictor</h1>
      <p class="lede">Tell us who's checking and we'll take you straight to the predictor.</p>

      <form class="card lead" (ngSubmit)="submitLead()" novalidate>
        <div class="field">
          <label for="lead-parent">Parent full name</label>
          <input id="lead-parent" name="parent" type="text" autocomplete="name"
            [(ngModel)]="lead.parent_full_name" (ngModelChange)="clearLeadError('parent_full_name')"
            [class.invalid]="leadErrors.parent_full_name" />
          <span class="field-error" *ngIf="leadErrors.parent_full_name">{{ leadErrors.parent_full_name }}</span>
        </div>

        <div class="field">
          <label for="lead-student">Student name</label>
          <input id="lead-student" name="student" type="text" autocomplete="off"
            [(ngModel)]="lead.student_name" (ngModelChange)="clearLeadError('student_name')"
            [class.invalid]="leadErrors.student_name" />
          <span class="field-error" *ngIf="leadErrors.student_name">{{ leadErrors.student_name }}</span>
        </div>

        <div class="field">
          <label for="lead-email">Email</label>
          <input id="lead-email" name="email" type="email" autocomplete="email" inputmode="email"
            [(ngModel)]="lead.email" (ngModelChange)="clearLeadError('email')"
            [class.invalid]="leadErrors.email" />
          <span class="field-error" *ngIf="leadErrors.email">{{ leadErrors.email }}</span>
        </div>

        <div class="field">
          <label for="lead-phone">Phone</label>
          <input id="lead-phone" name="phone" type="tel" autocomplete="tel" inputmode="tel"
            [(ngModel)]="lead.phone" (ngModelChange)="clearLeadError('phone')"
            [class.invalid]="leadErrors.phone" />
          <span class="field-error" *ngIf="leadErrors.phone">{{ leadErrors.phone }}</span>
        </div>

        <div class="field">
          <span class="field-label">Standard</span>
          <div class="chips" role="radiogroup" aria-label="Standard">
            <button type="button" class="chip" *ngFor="let s of standards"
              role="radio" [attr.aria-checked]="lead.standard === s"
              [class.chip-on]="lead.standard === s"
              (click)="lead.standard = s; clearLeadError('standard')">{{ s }}</button>
          </div>
          <span class="field-error" *ngIf="leadErrors.standard">{{ leadErrors.standard }}</span>
        </div>

        <p class="submit-error" *ngIf="leadError" role="alert">{{ leadError }}</p>

        <button class="primary" type="submit" [disabled]="leadSubmitting">
          <span class="spinner" *ngIf="leadSubmitting" aria-hidden="true"></span>
          {{ leadSubmitting ? 'Saving…' : 'Continue to predictor' }}
        </button>
      </form>
    </ng-container>

    <!-- ============ FORM ============ -->
    <ng-container *ngIf="hasLead">
    <ng-container *ngIf="!showResult; else resultTpl">

      <p class="eyebrow">Free tool</p>
      <h1 class="title">Free CSSE predictor</h1>
      <p class="lede">Slide each paper. Marks stay in your browser — nothing is submitted.</p>

      <section class="card sliders">
        <div class="slider" *ngFor="let s of subjects">
          <div class="slider-head">
            <label [for]="'in-' + s.key">{{ s.label }}</label>
            <div class="slider-value">
              <input
                class="num"
                type="number"
                inputmode="numeric"
                min="0"
                [max]="s.max"
                [value]="value(s.key)"
                (input)="onInput(s.key, $event, s.max)"
                (blur)="onInput(s.key, $event, s.max)"
                [attr.aria-label]="s.label + ' marks'" />
              <span class="of">/ {{ s.max }}</span>
            </div>
          </div>
          <div
            class="cp-slider"
            role="slider"
            tabindex="0"
            [attr.aria-label]="s.label"
            aria-valuemin="0"
            [attr.aria-valuemax]="s.max"
            [attr.aria-valuenow]="value(s.key)"
            (pointerdown)="dragStart(s.key, s.max, $event)"
            (keydown)="onKey(s.key, s.max, $event)">
            <div class="cp-slider-track">
              <div class="cp-slider-fill" [style.width.%]="(value(s.key) / s.max) * 100"></div>
            </div>
            <div class="cp-slider-thumb" [style.left.%]="(value(s.key) / s.max) * 100"></div>
          </div>
        </div>
      </section>

      <section class="card dob">
        <h2 class="card-title">Date of birth</h2>
        <p class="hint">Younger children get an age allowance on the standard score.</p>
        <div class="dob-row">
          <select [(ngModel)]="day" aria-label="Day">
            <option *ngFor="let d of days" [ngValue]="d">{{ d }}</option>
          </select>
          <select [(ngModel)]="month" aria-label="Month">
            <option *ngFor="let m of months; let i = index" [ngValue]="i + 1">{{ m }}</option>
          </select>
          <select [(ngModel)]="year" aria-label="Year">
            <option *ngFor="let y of years" [ngValue]="y">{{ y }}</option>
          </select>
        </div>
        <p class="age-note" *ngIf="live?.ageNote">{{ live?.ageNote }}</p>
      </section>

      <section class="live">
        <div>
          <p class="live-label">Live standard score</p>
          <p class="live-score">{{ (live?.finalScore ?? 0) | number: '1.1-1' }}</p>
        </div>
        <span class="live-level" *ngIf="live?.level">{{ live?.level }}</span>
      </section>

      <button class="primary" type="button" (click)="calculate()" [disabled]="!live">
        See the full prediction
      </button>
    </ng-container>

    <!-- ============ RESULT ============ -->
    <ng-template #resultTpl>
      <ng-container *ngIf="result as r">

        <div class="result-top">
          <button class="pill" type="button" (click)="edit()">← Back</button>
          <button class="pill pill-accent" type="button" (click)="edit()">Edit marks</button>
        </div>

        <section class="hero">
          <p class="hero-label">CSSE standard score</p>
          <div class="hero-score-row">
            <span class="hero-score">{{ r.finalScore | number: '1.1-1' }}</span>
            <span class="hero-level">{{ r.level }}</span>
          </div>

          <div class="hero-bar">
            <div class="hero-bar-fill" [style.width.%]="scale(r.finalScore)"></div>
          </div>
          <div class="hero-scale">
            <span>240</span><span>{{ r.bandNote }}</span><span>400</span>
          </div>

          <div class="hero-tiles">
            <div class="tile">
              <span class="tile-label">English</span>
              <span class="tile-value">{{ r.englishStandard | number: '1.1-1' }}</span>
            </div>
            <div class="tile">
              <span class="tile-label">Maths</span>
              <span class="tile-value">{{ r.mathsStandard | number: '1.1-1' }}</span>
            </div>
          </div>
        </section>

        <p class="coach" *ngIf="r.coachLine">{{ r.coachLine }}</p>

        <h2 class="section-title">All seven schools</h2>

        <div class="schools">
          <article class="school" *ngFor="let s of r.schools; trackBy: trackSchool" [attr.data-tone]="s.tone">
            <div class="school-head">
              <h3>{{ s.school }}</h3>
              <span class="status">{{ s.status }}</span>
            </div>
            <div class="bar">
              <div class="bar-fill" [style.width.%]="scale(s.studentScore)"></div>
              <div class="bar-tick" *ngIf="cutoffOf(s) !== null" [style.left.%]="scale(cutoffOf(s)!)"></div>
            </div>
            <div class="school-foot">
              <span class="cutoffs">{{ cutoffText(s) }}</span>
              <span class="gap">{{ gapText(s.diff) }}</span>
            </div>
          </article>
        </div>

        <p class="disclaimer">
          Based on 2025 CSSE standardisation and published cutoffs. Cutoffs change every year,
          so treat this as a guide, not a guarantee of a place.
        </p>
      </ng-container>
    </ng-template>
    </ng-container>

  </div>
</main>
